import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatTitle } from "@/lib/typography";
import { Glossaire } from "./Glossaire";
import { CopyButton } from "./CopyButton";
import { z } from "zod";

const kwSchema = z.object({
  query: z.string().min(1).max(200),
  market: z.enum(["FR", "DE", "UK", "ES", "IT"]),
  intent: z.enum(["transactionnel", "informatif", "marque"]),
});

interface KeywordResult {
  level: string;
  kw: string;
  vol: number;
  cpc: number;
  ctr: number;
}

export const KeywordsPage = () => {
  const [query, setQuery] = useState("botox capillaire");
  const [market, setMarket] = useState("FR");
  const [intent, setIntent] = useState("transactionnel");
  const [data, setData] = useState<KeywordResult[]>([]);

  const fakeFetch = () => {
    try {
      kwSchema.parse({ query, market, intent });
      const demo: KeywordResult[] = [
        { level: "Exact", kw: "botox capillaire", vol: 7500, cpc: 0.68, ctr: 1.2 },
        { level: "Phrase", kw: "masque botox cheveux", vol: 6200, cpc: 0.55, ctr: 1.1 },
        { level: "Broad", kw: "soin capillaire professionnel", vol: 12000, cpc: 0.4, ctr: 0.9 },
      ];
      setData(demo);
    } catch (err) {
      console.error("Validation error:", err);
    }
  };

  const intentions = [
    { id: "transactionnel", label: "Transactionnel", desc: "Acheter maintenant — ex. shampoing botox capillaire 500 ml" },
    { id: "informatif", label: "Informatif", desc: "Comparer — ex. meilleur shampoing botox 2025" },
    { id: "marque", label: "Marque", desc: "Ta marque et concurrents" },
  ];

  const glossaire = [
    { term: "Intent", definition: "Intention de recherche — transactionnel, informatif, marque" },
    { term: "Exact", definition: "Correspondance exacte — ciblage précis" },
    { term: "Phrase", definition: "Correspondance expression — variantes proches" },
    { term: "Broad", definition: "Correspondance large — exploration" },
    { term: "Harvest", definition: "Transfert des requêtes gagnantes vers Manuel" },
    { term: "Négatifs", definition: "Exclusion de requêtes non pertinentes" },
    { term: "CPC", definition: "Coût par clic" },
    { term: "CVR", definition: "Taux de conversion" },
  ];

  const exportByLevel = (level: string) => {
    return data.filter((d) => d.level === level).map((d) => d.kw).join(", ");
  };

  const exportText = `Exact — ${exportByLevel("Exact")}\nPhrase — ${exportByLevel("Phrase")}\nBroad — ${exportByLevel("Broad")}`;

  return (
    <div className="space-y-4">
      {/* 1) INTENTIONS */}
      <Card>
        <CardHeader>
          <CardTitle>{formatTitle("1) Intentions — classification")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {intentions.map((int) => (
              <div key={int.id} className="rounded-lg border p-3">
                <div className="font-semibold text-sm text-primary mb-1">{int.label}</div>
                <p className="text-xs text-muted-foreground">{int.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 2) GÉNÉRATEUR */}
      <Card>
        <CardHeader>
          <CardTitle>{formatTitle("2) Générateur — suggestions Exact, Phrase, Broad")}</CardTitle>
          <CardDescription>Paramètres de recherche</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="text-sm">
              Recherche / produit
              <input value={query} onChange={(e) => setQuery(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Marché
              <select value={market} onChange={(e) => setMarket(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground">
                <option value="FR">France</option>
                <option value="DE">Allemagne</option>
                <option value="UK">UK</option>
                <option value="ES">Espagne</option>
                <option value="IT">Italie</option>
              </select>
            </label>
            <label className="text-sm">
              Intention
              <select value={intent} onChange={(e) => setIntent(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground">
                <option value="transactionnel">Transactionnel</option>
                <option value="informatif">Informatif</option>
                <option value="marque">Marque</option>
              </select>
            </label>
          </div>
          <div className="flex gap-2">
            <Button onClick={fakeFetch} variant="outline">Générer (démo)</Button>
            {data.length > 0 && <CopyButton text={JSON.stringify(data, null, 2)} label="Copier JSON" />}
          </div>
        </CardContent>
      </Card>

      {data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{formatTitle("Suggestions (Exact / Phrase / Broad)")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-4">Niveau</th>
                    <th className="py-2 pr-4">Mot-clé</th>
                    <th className="py-2 pr-4">Volume</th>
                    <th className="py-2 pr-4">CPC (€)</th>
                    <th className="py-2 pr-4">CTR (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((r, i) => (
                    <tr key={i} className="border-b last:border-b-0">
                      <td className="py-2 pr-4">
                        <Badge variant="outline">{r.level}</Badge>
                      </td>
                      <td className="py-2 pr-4">{r.kw}</td>
                      <td className="py-2 pr-4 tabular-nums">{r.vol.toLocaleString("fr-FR")}</td>
                      <td className="py-2 pr-4 tabular-nums">{r.cpc.toFixed(2)}</td>
                      <td className="py-2 pr-4 tabular-nums">{r.ctr.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3) EXPORT */}
      {data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{formatTitle("3) Export")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <CopyButton text={exportText} label="Copier par niveau" />
            <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground border">
              {exportText}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* 4) GLOSSAIRE */}
      <Glossaire entries={glossaire} />
    </div>
  );
};
